import Button from 'components/Basic/Button';
import { clearToolModeHandlers, registerToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { useRefs } from 'contexts/RefContext';
import { useCancelRoofTool } from 'hooks/useCancelRoofTool';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { enableRoofToolLines } from 'store/slices/RoofToolLinesSlice';
import { LinesData } from 'store/slices/RoofToolLinesSlice/types';
import { useEffect } from 'react';
import useRoofToolFacet from 'hooks/api/facet/useUpdateRoofTool'; 
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice'; 
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { createInitialSelectedFacetVal } from 'store/slices/RoofToolLinesSlice/util';
import { selectFilteredGutterLines } from 'store/slices/RoofDataSlice/roofSelector';

export default function EdgeTool() {
	const { specialEdges, uuid, panel, designType, showAutoDetectedEdges } = useSelector((state: RootState) => state.roofData.data);
	const filteredGutterLines = useSelector(selectFilteredGutterLines);
	const { activeRoofTool, createSpecialEdges, updateSpecialEdges } = useSelector((state: RootState) => state.roofToolLines.data);
	const isAnyOtherActionEnabled = useSelector(selectIsAnyToolActionActive);
	const queriesRunningInBackground = useQueriesRunningInBackGround();

	const shouldDisableIfOtherAction = (!activeRoofTool && isAnyOtherActionEnabled) || queriesRunningInBackground;
	const dispatch = useDispatch<AppDispatch>();
	const updateRoofToolMutation = useRoofToolFacet(); 
	const { onConfirm} = useCancelRoofTool();

	const isSolarDesign = designType === 'SOLAR';

	const handleRoofToolClick = () => {
		const selectedFacets: LinesData['selectedFacets'] = {};
		if (!activeRoofTool) {
			for (const { type: lineType, edges, isSystemDetected } of specialEdges) {
				if(isSystemDetected && !showAutoDetectedEdges) continue;
				for (const [facetId, lineId] of Object.entries(edges)) {
					const facet = selectedFacets[facetId] ??= createInitialSelectedFacetVal();
					facet.selectedLines.push({ id: lineId, lineType , isSystemDetected});
				}
			}

			for(const gl of filteredGutterLines){
				if(!gl.roofSegmentId) continue;

				if(gl.roofSegmentId in selectedFacets){
					selectedFacets[gl.roofSegmentId].gutterLines.push(gl);
					continue;
				}

				selectedFacets[gl.roofSegmentId] = createInitialSelectedFacetVal();

				selectedFacets[gl.roofSegmentId].gutterLines.push(gl);
			}
		}
		dispatch(enableRoofToolLines({ shouldEnable: !activeRoofTool, initialData: selectedFacets }));
	};
	
	useEffect(() => {
		const cancelHandler = () => {
			dispatch(enableRoofToolLines({ shouldEnable: false, initialData: {} }));
		};

		const doneHandler = async () => {
			const hasChanges = createSpecialEdges.length > 0 || updateSpecialEdges.length > 0;

			if (!hasChanges) {
				dispatch(enableRoofToolLines({ shouldEnable: false, initialData: {} }));
				return;
			}

			await updateRoofToolMutation.mutateAsync();
			await dispatch(changePanel({ uuid, panelKey: panel.key, designType })).unwrap();

			dispatch(enableRoofToolLines({ shouldEnable: false, initialData: {} }));
		};

		registerToolModeHandlers(TOOL_ACTION.EDIT_EDGES, {
			cancel: cancelHandler,
			done: doneHandler,
			cancelConfirmMessage: 'Are you sure you want to discard the changes?',
		});

		return () => {
			clearToolModeHandlers(TOOL_ACTION.EDIT_EDGES);
		};
	}, [dispatch,createSpecialEdges,updateSpecialEdges,updateRoofToolMutation,uuid,panel?.key,designType]);

	return (
		<>
			<Button
				style={{
					background: `${activeRoofTool ? 'var(--toolActiveBgColor)' : 'var(--defaultBgColor)'}`,
					color: `${activeRoofTool ? 'var(--primary)' : 'var(--labelValueColor)'}`,
				}}
				id="edge-tool"
				disabled={shouldDisableIfOtherAction}
				onClick={handleRoofToolClick}
				// disabled='disabled'
				tooltipContent={`${activeRoofTool ? '' : 'Edit Edges'}`}
				className={`${activeRoofTool ? 'active ' : ''}btn-tools ${isSolarDesign ? 'btn-edgetool-solar' :'btn-edgetool'}`}>
				{/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="18" viewBox="0 0 23 17" fill="none">
					<path d="M17.799 4.59961L18.453 4.34992L18.2811 3.89961H17.799V4.59961ZM16.3991 14.8988C16.0125 14.8988 
					15.6991 15.2122 15.6991 15.5988C15.6991 15.9854 16.0125 16.2988 16.3991 16.2988V14.8988ZM21.7916 15.0562L21.1376 
					15.3059L21.7916 15.0562ZM5.30166 4.85691L5.95561 5.1066L5.30166 4.85691ZM1.40739 15.0562L0.753433 14.8065L1.40739 
					15.0562ZM13.8951 15.3356L14.5529 15.5748L13.8951 15.3356ZM17.799 4.59961L17.1412 4.36039L13.2372 15.0963L13.8951 
					15.3356L14.5529 15.5748L18.4569 4.83883L17.799 4.59961ZM13.5192 15.5988V14.8988H1.78105V15.5988V16.2988H13.5192V15.5988ZM1.40739 
					15.0562L2.06134 15.3059L5.95561 5.1066L5.30166 4.85691L4.64771 4.60722L0.753433 14.8065L1.40739 15.0562ZM17.799 4.59961L17.1451 
					4.8493L21.1376 15.3059L21.7916 15.0562L22.4455 14.8065L18.453 4.34992L17.799 4.59961ZM21.4179 
					15.5988V14.8988H16.3991V15.5988V16.2988H21.4179V15.5988ZM5.67532 4.59961V5.29961H7.59975V4.59961V3.89961H5.67532V4.59961ZM11.1995 
					4.59961V5.29961H17.799V4.59961V3.89961H11.1995V4.59961ZM21.7916 15.0562L21.1376 15.3059C21.0626 15.1095 21.2076 14.8988 21.4179 
					14.8988V15.5988V16.2988C22.1887 16.2988 22.7204 15.5266 22.4455 14.8065L21.7916 15.0562ZM5.30166 4.85691L5.95561 5.1066C5.91124 
					5.22283 5.79973 5.29961 5.67532 5.29961V4.59961V3.89961C5.2192 3.89961 4.8104 4.18111 4.64771 4.60722L5.30166 4.85691ZM1.78105 
					15.5988V14.8988C1.99129 14.8988 2.13633 15.1095 2.06134 15.3059L1.40739 15.0562L0.753433 14.8065C0.478489 15.5266 1.01025 16.2988 
					1.78105 16.2988V15.5988ZM13.8951 15.3356L13.2372 15.0963C13.2803 14.9778 13.393 14.8988 13.5192 14.8988V15.5988V16.2988C13.9817 
					16.2988 14.3948 16.0095 14.5529 15.5748L13.8951 15.3356Z" fill={`${open ? 'var(--primary)' : 'var(--defaultIconColor)'}`} />
					<path d="M7.99951 6.79925V1.99958C7.99951 1.77868 8.17859 1.59961 8.39948 1.59961H10.3993C10.6202 1.59961 10.7993 1.77868 10.7993 
					1.99958V6.79925C10.7993 7.02015 10.6202 7.19922 10.3993 7.19922H8.39948C8.17859 7.19922 7.99951 7.02015 7.99951 6.79925Z" 
					stroke={`${open ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.4"/>
				</svg> */}
				<svg xmlns="http://www.w3.org/2000/svg" width="21" height="16.5" viewBox="0 0 47 37" fill="none">
					<path d="M44.2131 2.26758H2V34.637H44.2131C44.9378 34.637 44.5151 13.0574 44.2131 2.26758Z"
						stroke={`${activeRoofTool ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="3.02972" strokeLinejoin="round" />
					<path d="M44.2095 34.637L32.3125 18.6335L44.2095 2.26758"
						stroke={`${activeRoofTool ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="3.02972" strokeLinecap="round" strokeDasharray="2.21 4.42" />
					<path d="M13.9648 18.8184H32.2632" stroke={`${activeRoofTool ? 'var(--primary)' : 'var(--defaultIconColor)'}`}
						strokeWidth="3.02972" strokeLinecap="round" strokeDasharray="2.21 4.42" />
					<path d="M2.24755 34.637L14.1445 18.6335L2.24755 2.26758"
						stroke={`${activeRoofTool ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="3.02972" strokeLinecap="round" strokeDasharray="2.21 4.42" />
				</svg>
				{/* <svg 
					width={'25'}
					height={'18'}
					viewBox="0 0 25 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M19.6153 10.3085L23.6043 16.9568H1L6.92018 7.64917H10.3077" 
						stroke={`${enableEditFacet ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M19.742 2.29927L21.0413 1L23.1308 3.08948L21.8807 4.33956M19.742 2.29927L12.6834 9.35791L11.6387 12.4921L14.7729 
						11.4474L21.8807 4.33956M19.742 2.29927L21.8807 4.33956" 
					stroke={`${enableEditFacet ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
				</svg> */}
				{/* {selected ? `: ${selected}` : ''} */}
			</Button>
		</>
	);

}